import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import InputBox from "../InputBox/InputBox";
import Button from "../Button/Button";
import styles from "../ProfileForm/ProfileForm.module.css";
import environment from "../../environment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";

export const ProfileForm = ({
  initialFirstName,
  initialLastName,
  initialPosition,
  initialEquipment,
  initialBase,
  initialEmployeeNumber,
  initialReferrersEmployeeNumber,
  initialEmail,
  initialPhoneNumber,
  initialPassword,
  initialConfirmPassword,
  profileEditable = false,
  isSignUp = true,
  onCancel,
  onError,
  onSave,
  isPasswordReset,
}) => {
  // Hooks
  const [firstName, setFirstName] = useState(initialFirstName || "");
  const [lastName, setLastName] = useState(initialLastName || "");
  const [position, setPosition] = useState(initialPosition || "");
  const [equipment, setEquipment] = useState(initialEquipment || "");
  const [base, setBase] = useState(initialBase || "");
  const [employeeNumber, setEmployeeNumber] = useState(
    initialEmployeeNumber || ""
  );
  const [referrersEmployeeNumber, setReferrersEmployeeNumber] = useState(
    initialReferrersEmployeeNumber || ""
  );
  const [Email, setEmail] = useState(initialEmail || "");
  const [Password, setPassword] = useState(initialPassword || "");
  const [ConfirmPassword, setConfirmPassword] = useState(
    initialConfirmPassword || ""
  );
  const [passwordBlurred, setPasswordBlurred] = useState(false);
  const [confirmPasswordBlurred, setConfirmPasswordBlurred] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber || "");

  useEffect(() => {
    setFirstName(initialFirstName || "");
    setLastName(initialLastName || "");
    setPosition(initialPosition || "");
    setEquipment(initialEquipment || "");
    setBase(initialBase || "");
    setEmployeeNumber(initialEmployeeNumber || "");
    setReferrersEmployeeNumber(initialReferrersEmployeeNumber || "");
    setEmail(initialEmail || "");
    setPhoneNumber(initialPhoneNumber || "");
    setPassword(initialPassword || "");
    setConfirmPassword(initialConfirmPassword || "");
  }, [
    initialFirstName,
    initialLastName,
    initialPosition,
    initialEquipment,
    initialBase,
    initialEmployeeNumber,
    initialReferrersEmployeeNumber,
    initialEmail,
    initialPhoneNumber,
    initialPassword,
    initialConfirmPassword,
  ]);

  // Dropdown states and filtered options
  const [isPositionDropdownOpen, setIsPositionDropdownOpen] = useState(false);
  const [isEquipmentDropdownOpen, setIsEquipmentDropdownOpen] = useState(false);
  const [isBaseDropdownOpen, setIsBaseDropdownOpen] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);

  const positionOptions = ["F/O", "CPT"]; // all position options
  const equipmentOptions = ["M1F", "757", "A30", "74Y"]; // all equipment options
  const baseOptions = ["SDF", "ANC", "MIA", "ONT", "SDFZ"]; // all base options

  const [filteredPosition, setFilteredPosition] = useState([
    ...positionOptions,
  ]);
  const [filteredEquipment, setFilteredEquipment] = useState([
    ...equipmentOptions,
  ]);
  const [filteredBase, setFilteredBase] = useState([...baseOptions]);

  const [positionSearchMessage, setPositionSearchMessage] = useState(null);
  const [equipmentSearchMessage, setEquipmentSearchMessage] = useState(null);
  const [baseSearchMessage, setBaseSearchMessage] = useState(null);

  const [passwordError, setPasswordError] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [showErrorComponent, setShowErrorComponent] = useState(false);

  const showError = (message) => {
    setErrorMessage(message);

    setTimeout(() => {
      setErrorMessage(null);
    }, 3000);
  };
  const showErrorNoTimer = (message) => {
    setErrorMessage(message);
  };
  // Toggle dropdowns and close others
  const togglePositionDropdown = () => {
    setIsPositionDropdownOpen(!isPositionDropdownOpen);
    setIsEquipmentDropdownOpen(false); // Close equipment dropdown
    setIsBaseDropdownOpen(false); // Close base dropdown
  };

  const toggleEquipmentDropdown = () => {
    setIsEquipmentDropdownOpen(!isEquipmentDropdownOpen);
    setIsPositionDropdownOpen(false); // Close position dropdown
    setIsBaseDropdownOpen(false); // Close base dropdown
  };

  const toggleBaseDropdown = () => {
    setIsBaseDropdownOpen(!isBaseDropdownOpen);
    setIsPositionDropdownOpen(false); // Close position dropdown
    setIsEquipmentDropdownOpen(false); // Close equipment dropdown
  };

  // Select dropdown items
  const handlePositionSelect = (value) => {
    setPosition(value);
    setIsPositionDropdownOpen(false);
  };

  const handleEquipmentSelect = (value) => {
    setEquipment(value);
    setIsEquipmentDropdownOpen(false);
  };

  const handleBaseSelect = (value) => {
    setBase(value);
    setIsBaseDropdownOpen(false);
  };

  // Handle functions for input changes
  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e) => {
    setLastName(e.target.value);
  };

  const handleEmployeeNumberChange = (e) => {
    setEmployeeNumber(e.target.value);
  };

  const handleReferrersEmployeeNumberChange = (e) => {
    setReferrersEmployeeNumber(e.target.value);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setPhoneNumber(value);
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
  };
  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);

    if (value && value !== Password) {
      setPasswordError("Passwords do not match.");
    } else {
      setPasswordError(""); // Clear the error if they match
    }
  };
  const validatePassword = (password) => {
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*?_~]).{6,}$/;
    return passwordRegex.test(password);
  };
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  const cleanPhoneNumber = (number) => {
    return number.replace(/\D/g, ""); // Remove all non-digits
  };

  const validatePhoneNumber = (number) => {
    const cleaned = cleanPhoneNumber(number);
    return /^\d{10}$/.test(cleaned);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // FIX SIGNUP PROCESS IF PASSWORD WAS ALREADY CHANGED

    if (isSignUp) {
      if (
        !firstName ||
        !lastName ||
        !position ||
        !equipment ||
        !base ||
        !Email ||
        !phoneNumber ||
        (!Password && isPasswordReset) ||
        (!ConfirmPassword && isPasswordReset)
      ) {
        showErrorNoTimer("Some fields are empty");
        return;
      }
      if (!validateEmail(Email)) {
        showErrorNoTimer("Please enter a valid email address.");
        return;
      }
      if (!validatePhoneNumber(phoneNumber)) {
        showErrorNoTimer("Please enter a valid phone number.");
        return;
      }

      if (isPasswordReset && Password !== ConfirmPassword) {
        showErrorNoTimer("Passwords do not match. Please try again.");
        //setErrorMessage("Passwords do not match. Please try again.");

        return;
      }

      if (isPasswordReset && !validatePassword(Password)) {
        showErrorNoTimer(
          "Password must contain at least one capital letter, one lowercase letter, a number, and a special character from the set: [! @ # $ % ^ & * ? _ ~]. Password must contain at least 6 characters."
        );
        return;
      } else {
        setPasswordError(""); // Clear the error if valid
      }

      //extra layer to ensure password is reset when required
      if (isPasswordReset && !Password && !ConfirmPassword) {
        showErrorNoTimer("You must reset your password.");
        return;
      }

      let formData;
      const cleanedPhone = cleanPhoneNumber(phoneNumber);
      if (isPasswordReset || Password) {
        formData = {
          firstName,
          lastName,
          position,
          equipment,
          base,
          employeeNumber,
          referrersEmployeeNumber,
          email: Email,
          phoneNumber: cleanedPhone,
          password: Password,
        };
      } else {
        formData = {
          firstName,
          lastName,
          position,
          equipment,
          base,
          employeeNumber,
          referrersEmployeeNumber,
          email: Email,
          phoneNumber: cleanedPhone,
        };
      }

      try {
        // Post data to the API endpoint
        /* const response = await fetch(
        "http://localhost:7071/api/UniqueUserCheck",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
          cache: "no-store",
        }
      );

      const result = await response.json();
      
       if (result.success) {
        onSave(formData);
      } else {
        onError();
      }
      */

        if (isPasswordReset || Password) {
          const response = await fetch(environment.apiBase + "/UserPassCheck", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
            cache: "no-store",
            credentials: "include",
          });

          if (response.status == 200) {
            onSave(formData);
          } else {
            if (response.status == 409) {
              showErrorNoTimer("Cannot use old password!");
            }
          }
        } else {
          onSave(formData);
        }
      } catch (error) {
        setErrorMessage(
          "An error occurred while submitting the form. Please try again."
        );
      }
    } else {
      const formData = {};

      // Collect data only if fields are modified
      if (firstName && firstName !== initialFirstName)
        formData.firstName = firstName;
      if (lastName && lastName !== initialLastName)
        formData.lastName = lastName;
      if (position && position !== initialPosition)
        formData.position = position;
      if (equipment && equipment !== initialEquipment)
        formData.equipment = equipment;
      if (base && base !== initialBase) formData.base = base;
      if (employeeNumber && employeeNumber !== initialEmployeeNumber)
        if (
          referrersEmployeeNumber &&
          referrersEmployeeNumber !== initialReferrersEmployeeNumber
        )
          // formData.employeeNumber = employeeNumber;
          formData.referrersEmployeeNumber = referrersEmployeeNumber;
      if (Email && Email !== initialEmail) {
        if (!validateEmail(Email)) {
          showErrorNoTimer("Please enter a valid email address.");
          return;
        }
        formData.email = Email;
      }

      if (initialPhoneNumber == null) {
        if (phoneNumber) {
          const cleanedPhone = cleanPhoneNumber(phoneNumber);

          if (!validatePhoneNumber(cleanedPhone)) {
            showErrorNoTimer("Please enter a valid phone number.");
            return;
          }

          formData.phoneNumber = cleanedPhone;
        } else {
          showErrorNoTimer("Phone number is required.");
          return;
        }
      } else {
        if (phoneNumber && phoneNumber !== initialPhoneNumber) {
          const cleanedPhone = cleanPhoneNumber(phoneNumber);

          if (!validatePhoneNumber(cleanedPhone)) {
            showErrorNoTimer("Please enter a valid phone number.");
            return;
          }

          formData.phoneNumber = cleanedPhone;
        }
      }

      //extra layer to ensure password is reset when required
      if (isPasswordReset) {
        if (!Password && !ConfirmPassword) {
          showErrorNoTimer("You must reset your password.");
          return;
        }
        if (Password !== ConfirmPassword) {
          showErrorNoTimer("Passwords do not match. Please try again.");
          return;
        }
        if (!validatePassword(Password)) {
          showErrorNoTimer(
            "Password must contain at least one capital letter, one lowercase letter, a number, and a special character from the set: [! @ # $ % ^ & * ? _ ~]. Password must contain at least 6 characters."
          );
          return;
        } else {
          setPasswordError(""); // Clear the error if valid
          formData.password = Password;
        }
      } else if (isEditingPassword) {
        // Only validate if the user clicked "Update Password"
        if (!Password || !ConfirmPassword) {
          showErrorNoTimer("Please complete both password fields.");
          return;
        }
        if (Password !== ConfirmPassword) {
          showErrorNoTimer("Passwords do not match. Please try again.");
          return;
        }
        if (!validatePassword(Password)) {
          showErrorNoTimer(
            "Password must contain at least one capital letter, one lowercase letter, a number, and a special character from the set: [! @ # $ % ^ & * ? _ ~]. Password must contain at least 6 characters."
          );
          return;
        } else {
          setPasswordError(""); // Clear the error if valid
          formData.password = Password;
        }
      }

      try {
        if (Object.keys(formData).length !== 0) {
          onSave(formData);
        } else {
          showErrorNoTimer("No fields were updated.");
        }
      } catch (error) {
        onError(error);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={`${styles.container} column`}>
        {errorMessage && (
          <div className={styles.spacing}>
            <div className={styles.customAlert} role="alert">
              {errorMessage}
            </div>
          </div>
        )}
        <div className="row">
          <InputBox
            id="firstName"
            className={styles.inputField}
            label="First Name*"
            placeholder="Enter your first name"
            setValue={(e) => {}}
            value={firstName}
            disabled={true}
          />
          <InputBox
            id="lastName"
            className={styles.inputField}
            label="Last Name*"
            placeholder="Enter your last name"
            setValue={(e) => {}}
            value={lastName}
            disabled={true}
          />
        </div>
        <div className={styles.airlineInput}>
          <InputBox
            id="airline"
            className={styles.inputField}
            label="Airline*"
            placeholder="Airline Name"
            setValue={(e) => {}}
            value={"UPS"}
            disabled={true}
          />
        </div>
        <div className="row">
          <div className="col-md-4">
            <label htmlFor="positionDropdown">Position*</label>
            <div className="dropdown">
              <button
                className={`btn btn-outline-dark ${styles.dropdownToggle}`}
                type="button"
                id="positionDropdown"
                onClick={togglePositionDropdown}
                disabled={!profileEditable}
              >
                <span>{position ? position : "Select Position"}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={styles.chevron}
                />
              </button>
              <ul
                className={`dropdown-menu ${
                  isPositionDropdownOpen ? "show" : ""
                }`}
              >
                {positionSearchMessage ? (
                  <li>
                    <span className="dropdown-item disabled">
                      {positionSearchMessage}
                    </span>
                  </li>
                ) : (
                  filteredPosition.map((item) => (
                    <li key={item}>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => handlePositionSelect(item)}
                      >
                        {item}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
          <div className="col-md-4">
            <label htmlFor="equipmentDropdown">Equipment*</label>
            <div className="dropdown">
              <button
                className={`btn btn-outline-dark ${styles.dropdownToggle}`}
                type="button"
                id="equipmentDropdown"
                onClick={toggleEquipmentDropdown}
                disabled={!profileEditable}
              >
                <span>{equipment ? equipment : "Select Equipment"}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={styles.chevron}
                />
              </button>
              <ul
                className={`dropdown-menu ${
                  isEquipmentDropdownOpen ? "show" : ""
                }`}
              >
                {equipmentSearchMessage ? (
                  <li>
                    <span className="dropdown-item disabled">
                      {equipmentSearchMessage}
                    </span>
                  </li>
                ) : (
                  filteredEquipment.map((item) => (
                    <li key={item}>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => handleEquipmentSelect(item)}
                      >
                        {item}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
          <div className="col-md-4">
            <label htmlFor="baseDropdown">Base*</label>
            <div className="dropdown">
              <button
                className={`btn btn-outline-dark ${styles.dropdownToggle}`}
                type="button"
                id="baseDropdown"
                onClick={toggleBaseDropdown}
                disabled={!profileEditable}
              >
                <span>{base ? base : "Select Base"}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={styles.chevron}
                />
              </button>
              <ul
                className={`dropdown-menu ${isBaseDropdownOpen ? "show" : ""}`}
              >
                {baseSearchMessage ? (
                  <li>
                    <span className="dropdown-item disabled">
                      {baseSearchMessage}
                    </span>
                  </li>
                ) : (
                  filteredBase.map((item) => (
                    <li key={item}>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => handleBaseSelect(item)}
                      >
                        {item}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>

        <div
          className={`${styles.breakEarly} row`}
          style={{ marginTop: "10px" }}
        >
          <InputBox
            id="PhoneNumber"
            className={styles.inputField}
            label="Phone Number*"
            placeholder="Enter your phone number"
            setValue={handlePhoneNumberChange}
            value={phoneNumber}
            disabled={!profileEditable}
            type="text"
          />
          <InputBox
            id="employeeNumber"
            className={styles.inputField}
            label="Employee Number*"
            placeholder="Employee Number"
            setValue={handleEmployeeNumberChange}
            value={employeeNumber}
            disabled={true}
          />
          <InputBox
            id="referrersEmployeeNumber"
            className={styles.inputField}
            label="Referrer's Employee Number"
            placeholder="Referred by another pilot?"
            setValue={handleReferrersEmployeeNumberChange}
            value={referrersEmployeeNumber}
            validationMessage={""}
            notRequired={true}
          />
        </div>

        {/* ✅ Password Logic: Editable vs View Only */}
        {isPasswordReset || isEditingPassword ? (
          <>
            <div className="row">
              <div className="col-md-4 d-flex flex-column">
                <InputBox
                  id="Email"
                  className={styles.inputField}
                  label="Email*"
                  placeholder="Enter your email"
                  setValue={handleEmailChange}
                  value={Email}
                  disabled={!profileEditable}
                />
              </div>

              <div className="col-md-4 d-flex flex-column">
                <InputBox
                  id="Password"
                  className={styles.inputField}
                  label="Enter New Password*"
                  placeholder="Create your password"
                  setValue={handlePasswordChange}
                  value={Password}
                  type="password"
                  notRequired={!isPasswordReset}
                  onBlur={() => setPasswordBlurred(true)}
                  validationMessage={
                    isPasswordReset && passwordBlurred && !Password
                      ? "You must enter a new password."
                      : ""
                  }
                />
              </div>

              <div className="col-md-4 d-flex flex-column">
                <InputBox
                  id="ConfirmPassword"
                  className={styles.inputField}
                  label="Confirm New Password*"
                  placeholder="Confirm your password"
                  setValue={handleConfirmPasswordChange}
                  value={ConfirmPassword}
                  type="password"
                  notRequired={!Password}
                  onBlur={() => setConfirmPasswordBlurred(true)}
                  validationMessage={
                    passwordError ||
                    (Password && confirmPasswordBlurred && !ConfirmPassword
                      ? "Please confirm your password."
                      : "")
                  }
                />
              </div>
            </div>

            {isEditingPassword && (
              <div className="row">
                <div className="col-md-12 d-flex justify-content-end mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditingPassword(false);
                      setPassword("");
                      setConfirmPassword("");
                      setPasswordError("");
                      setPasswordBlurred(false);
                      setConfirmPasswordBlurred(false);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      fontSize: "0.9rem",
                      color: "#6c757d", // Gray
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    Keep old password
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="row">
            <div className="col-md-4 d-flex flex-column">
              <InputBox
                id="Email"
                className={styles.inputField}
                label="Email*"
                placeholder="Enter your email"
                setValue={handleEmailChange}
                value={Email}
                disabled={!profileEditable}
              />
            </div>

            <div className="col-md-4 d-flex flex-column">
              <InputBox
                id="maskedPassword"
                className={styles.inputField}
                label="Password"
                value="********"
                disabled={true}
                setValue={() => {}}
              />
            </div>

            <div
              className="col-md-4 d-flex flex-column"
              style={{ paddingTop: "36px" }}
            >
              <button
                type="button"
                onClick={() => {
                  setIsEditingPassword(true);
                  setPassword("");
                  setConfirmPassword("");
                  setPasswordError("");
                  setPasswordBlurred(false);
                  setConfirmPasswordBlurred(false);
                }}
                style={{
                  width: "100%",
                  height: "40px",
                  backgroundColor: "#29abe2",
                  border: "1px solid #007bff",
                  color: "#fff",
                  borderRadius: "3px",
                  fontWeight: "500",
                }}
              >
                Update Password
              </button>
            </div>
          </div>
        )}

        <small>Fields marked with * are required</small>

        <div className={styles.formAction}>
          <Button label="Cancel" onClick={onCancel} />
          <div className={styles.buttonGap}></div>
          <Button label="Save Changes" type="submit" onClick={() => {}} />
        </div>
      </div>
    </form>
  );
};

ProfileForm.propTypes = {
  initialFirstName: PropTypes.string,
  initialLastName: PropTypes.string,
  initialPosition: PropTypes.string,
  initialEquipment: PropTypes.string,
  initialBase: PropTypes.string,
  initialEmployeeNumber: PropTypes.string,
  initialReferrersEmployeeNumber: PropTypes.string,
  initialEmail: PropTypes.string,
  initialPassword: PropTypes.string,
  initialConfirmPassword: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func,
};
